import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { unwrapList } from '../utils/unwrapList'

/** One of the caller's own login departments, resolved to a display name. */
export interface LoginDepartment {
  departmentId: number
  departmentName: string
  facilityId: number
}

/** `/departments` shape, trimmed to what a switcher needs. */
interface DepartmentRow {
  departmentId: number
  departmentName: string
  facilityId: number
}

/**
 * The caller's login departments (F3), named, plus which one this session is
 * working in.
 *
 * The id list and the active id come from the SERVER — the session store is
 * hydrated from `/auth/profile`, which reads the assignments from the database
 * and the active department from the presented token. Only the *names* are
 * looked up here, and only when the account actually holds a department: the
 * overwhelming majority hold none, and they must not pay a 500-row fetch to
 * render a control that will not appear.
 */
export function useLoginDepartments() {
  const user = useAuthStore((s) => s.user)
  const departmentIds = user?.departmentIds ?? []
  const activeDepartmentId = user?.activeDepartmentId ?? null

  const { data: all, isLoading } = useQuery({
    queryKey: ['departments-list'],
    // `unwrapList` because the gateway's list endpoints are inconsistent about
    // envelopes; `/departments` returns a bare array today.
    queryFn: async () =>
      unwrapList<DepartmentRow>((await apiClient.get('/departments')).data),
    enabled: departmentIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Preserve the server's order: `get_user_departments` sorts the primary
  // first, so the list reads the way the account is configured.
  const departments: LoginDepartment[] = departmentIds.map((id) => {
    const match = all?.find((d) => d.departmentId === id)
    return {
      departmentId: id,
      // No name yet (still loading, or the row is not in the list): fall back
      // to the id rather than to an empty string, which would render as a
      // blank control and read as missing data.
      departmentName: match?.departmentName ?? '',
      facilityId: match?.facilityId ?? 0,
    }
  })

  const active = departments.find((d) => d.departmentId === activeDepartmentId) ?? null

  return {
    departments,
    activeDepartmentId,
    active,
    /** True once the account holds departments but none is active — the state
     *  the design calls "the client prompts". It is NOT an error: login must
     *  never block on a department. */
    needsSelection: departmentIds.length > 0 && activeDepartmentId === null,
    isLoadingNames: isLoading,
  }
}
